/**
 * Small procedural sound-effect synthesizer built on raw Web Audio nodes.
 * Exists so the game has real, working audio out of the box without
 * shipping any licensed/recorded sample files — see ASSETS.md. Every
 * function schedules a short-lived graph (oscillators/noise -> filter ->
 * envelope -> destination) and lets it self-clean via `stop()`/GC.
 */

let noiseBufferCache: AudioBuffer | null = null;

function getNoiseBuffer(ctx: AudioContext): AudioBuffer {
  if (noiseBufferCache && noiseBufferCache.sampleRate === ctx.sampleRate) return noiseBufferCache;
  const duration = 1;
  const buffer = ctx.createBuffer(1, ctx.sampleRate * duration, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
  noiseBufferCache = buffer;
  return buffer;
}

function envelope(
  ctx: AudioContext,
  gain: GainNode,
  { attack, hold = 0, release, peak = 1 }: { attack: number; hold?: number; release: number; peak?: number }
): number {
  const now = ctx.currentTime;
  gain.gain.cancelScheduledValues(now);
  gain.gain.setValueAtTime(0, now);
  gain.gain.linearRampToValueAtTime(peak, now + attack);
  gain.gain.setValueAtTime(peak, now + attack + hold);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + attack + hold + release);
  return now + attack + hold + release;
}

/** A single tone with a pitch envelope, e.g. attack swings and UI blips. */
export function playTone(
  ctx: AudioContext,
  dest: AudioNode,
  opts: {
    type?: OscillatorType;
    startFreq: number;
    endFreq?: number;
    duration: number;
    volume?: number;
  }
): void {
  const osc = ctx.createOscillator();
  osc.type = opts.type ?? "sine";
  const gain = ctx.createGain();
  osc.connect(gain).connect(dest);

  const now = ctx.currentTime;
  osc.frequency.setValueAtTime(opts.startFreq, now);
  if (opts.endFreq && opts.endFreq !== opts.startFreq) {
    osc.frequency.exponentialRampToValueAtTime(Math.max(1, opts.endFreq), now + opts.duration);
  }

  const end = envelope(ctx, gain, {
    attack: Math.min(0.02, opts.duration * 0.2),
    release: opts.duration,
    peak: opts.volume ?? 0.5,
  });
  osc.start(now);
  osc.stop(end + 0.05);
}

/** An ascending/descending arpeggio — used for mission-complete and similar stingers. */
export function playArpeggio(
  ctx: AudioContext,
  dest: AudioNode,
  freqs: number[],
  noteDuration = 0.14,
  type: OscillatorType = "triangle"
): void {
  freqs.forEach((freq, i) => {
    const osc = ctx.createOscillator();
    osc.type = type;
    const gain = ctx.createGain();
    osc.connect(gain).connect(dest);
    osc.frequency.value = freq;

    const start = ctx.currentTime + i * noteDuration * 0.85;
    gain.gain.setValueAtTime(0, start);
    gain.gain.linearRampToValueAtTime(0.45, start + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + noteDuration);
    osc.start(start);
    osc.stop(start + noteDuration + 0.05);
  });
}

/** Filtered noise burst — digital "explosion"/impact texture. */
export function playNoiseBurst(
  ctx: AudioContext,
  dest: AudioNode,
  opts: { duration: number; filterStart: number; filterEnd: number; volume?: number }
): void {
  const src = ctx.createBufferSource();
  src.buffer = getNoiseBuffer(ctx);
  src.loop = true;

  const filter = ctx.createBiquadFilter();
  filter.type = "bandpass";
  filter.Q.value = 0.9;
  const now = ctx.currentTime;
  filter.frequency.setValueAtTime(opts.filterStart, now);
  filter.frequency.exponentialRampToValueAtTime(Math.max(40, opts.filterEnd), now + opts.duration);

  const gain = ctx.createGain();
  src.connect(filter).connect(gain).connect(dest);

  const end = envelope(ctx, gain, {
    attack: 0.01,
    release: opts.duration,
    peak: opts.volume ?? 0.5,
  });
  src.start(now);
  src.stop(end + 0.05);
}

/** A continuous, gently modulating ambient drone. Call the returned `stop()` to tear it down. */
export function startAmbientDrone(ctx: AudioContext, dest: AudioNode, volume = 0.18): { stop: () => void } {
  const gain = ctx.createGain();
  gain.gain.value = volume;
  gain.connect(dest);

  const baseFreqs = [55, 55 * 1.5, 55 * 2.006]; // root + fifth + slightly detuned octave, sci-fi pad
  const oscillators = baseFreqs.map((freq, i) => {
    const osc = ctx.createOscillator();
    osc.type = i === 0 ? "sine" : "triangle";
    osc.frequency.value = freq;
    const oscGain = ctx.createGain();
    oscGain.gain.value = i === 0 ? 1 : 0.35;
    osc.connect(oscGain).connect(gain);
    osc.start();
    return osc;
  });

  // Slow filter LFO sweep for movement, avoids a static/boring drone.
  const filter = ctx.createBiquadFilter();
  filter.type = "lowpass";
  filter.frequency.value = 400;
  gain.disconnect();
  gain.connect(filter).connect(dest);

  const lfo = ctx.createOscillator();
  lfo.frequency.value = 0.05;
  const lfoGain = ctx.createGain();
  lfoGain.gain.value = 220;
  lfo.connect(lfoGain).connect(filter.frequency);
  lfo.start();

  return {
    stop: () => {
      const now = ctx.currentTime;
      gain.gain.cancelScheduledValues(now);
      gain.gain.setValueAtTime(gain.gain.value, now);
      gain.gain.linearRampToValueAtTime(0, now + 0.6);
      window.setTimeout(() => {
        oscillators.forEach((o) => o.stop());
        lfo.stop();
      }, 700);
    },
  };
}
